import { useEffect, useState } from "react";
import { Users, Mail, Search, BadgeCheck, Gift, Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingDiscountId, setEditingDiscountId] = useState(null);
  const [discountValue, setDiscountValue] = useState("");
  const [savingDiscount, setSavingDiscount] = useState(false);

  const refresh = async (q) => {
    setLoading(true);
    try {
      const url = q ? `/admin/users?q=${encodeURIComponent(q)}` : "/admin/users";
      const { data } = await api.get(url);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(() => refresh(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditValue(u.referral_code || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (userId) => {
    const code = editValue.trim().toUpperCase();
    if (!/^[A-Z0-9]{4,20}$/.test(code)) {
      toast.error("Code invalide : 4 à 20 caractères, lettres et chiffres uniquement.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userId}/referral-code`, { code });
      setItems((prev) => prev.map((u) => (u.id === userId ? { ...u, referral_code: code } : u)));
      toast.success("Code de parrainage mis à jour.");
      cancelEdit();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setSaving(false);
  };

  const startEditDiscount = (u) => {
    setEditingDiscountId(u.id);
    setDiscountValue(u.referral_discount_percent != null ? String(u.referral_discount_percent) : "");
  };

  const cancelEditDiscount = () => {
    setEditingDiscountId(null);
    setDiscountValue("");
  };

  const saveDiscount = async (userId, resetToDefault = false) => {
    const percent = resetToDefault ? null : parseFloat(discountValue);
    if (!resetToDefault && (isNaN(percent) || percent < 0 || percent > 100)) {
      toast.error("Pourcentage invalide (0 à 100).");
      return;
    }
    setSavingDiscount(true);
    try {
      await api.patch(`/admin/users/${userId}/referral-discount`, { percent });
      setItems((prev) => prev.map((u) => (u.id === userId ? { ...u, referral_discount_percent: percent } : u)));
      toast.success(percent === null ? "Réduction remise par défaut." : "Réduction personnalisée enregistrée.");
      cancelEditDiscount();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setSavingDiscount(false);
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Users size={18} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold">Utilisateurs inscrits</h1>
          <p className="text-sm text-gray-500"><span className="font-semibold">{total}</span> {total > 1 ? "comptes" : "compte"} sur Swiftlink-Africa.</p>
        </div>
      </div>

      <div className="sl-card mb-4 flex items-center gap-2">
        <Search size={16} className="text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher par nom ou email…"
          className="flex-1 outline-none text-sm bg-transparent"
          data-testid="users-search"
        />
      </div>

      {loading ? (
        <div className="sl-card text-center text-gray-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="sl-card text-center text-sm text-gray-500" data-testid="users-empty">
          Aucun utilisateur trouvé.
        </div>
      ) : (
        <div className="sl-card overflow-x-auto" data-testid="users-table">
          <table className="w-full min-w-[700px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3">Utilisateur</th>
                <th className="py-2 pr-3">Téléphone</th>
                <th className="py-2 pr-3">Parrain</th>
                <th className="py-2 pr-3 text-right">Tx</th>
                <th className="py-2 pr-3">Code perso</th>
                <th className="py-2 pr-3">Réduction</th>
                <th className="py-2 pr-3">Inscrit le</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-b border-gray-50" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 pr-3">
                    <div className="font-medium">{u.name || "—"}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <Mail size={10} /> {u.email}
                      {u.email_verified && <BadgeCheck size={11} className="text-emerald-600" />}
                    </div>
                  </td>
                  <td className="py-3 pr-3 text-xs font-mono text-gray-700">{u.phone || "—"}</td>
                  <td className="py-3 pr-3 text-xs">
                    {u.referred_by_code ? (
                      <div>
                        <div className="font-mono font-semibold text-[var(--sl-primary)]">{u.referred_by_code}</div>
                        {u.referred_by_email && <div className="text-[10px] text-gray-500 truncate">{u.referred_by_email}</div>}
                      </div>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-3 pr-3 text-right font-semibold">{u.tx_count}</td>
                  <td className="py-3 pr-3">
                    {editingId === u.id ? (
                      <div className="flex items-center gap-1" data-testid={`referral-code-edit-${u.id}`}>
                        <input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value.toUpperCase())}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(u.id); if (e.key === "Escape") cancelEdit(); }}
                          maxLength={20}
                          autoFocus
                          className="w-28 text-xs font-mono uppercase border border-[var(--sl-primary)] rounded px-1.5 py-1 outline-none"
                          data-testid={`referral-code-input-${u.id}`}
                        />
                        <button
                          onClick={() => saveEdit(u.id)}
                          disabled={saving}
                          className="w-6 h-6 rounded grid place-items-center text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                          data-testid={`referral-code-save-${u.id}`}
                          aria-label="Enregistrer"
                        >
                          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="w-6 h-6 rounded grid place-items-center text-gray-400 hover:bg-gray-100"
                          data-testid={`referral-code-cancel-${u.id}`}
                          aria-label="Annuler"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 group">
                        <span className="inline-flex items-center gap-1 text-xs font-mono bg-[var(--sl-bg-alt)] px-2 py-0.5 rounded">
                          <Gift size={10} /> {u.referral_code}
                        </span>
                        <button
                          onClick={() => startEdit(u)}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-[var(--sl-primary)] transition-opacity"
                          data-testid={`referral-code-edit-btn-${u.id}`}
                          aria-label="Personnaliser le code de parrainage"
                        >
                          <Pencil size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="py-3 pr-3">
                    {editingDiscountId === u.id ? (
                      <div className="flex items-center gap-1" data-testid={`referral-discount-edit-${u.id}`}>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={discountValue}
                          onChange={(e) => setDiscountValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveDiscount(u.id); if (e.key === "Escape") cancelEditDiscount(); }}
                          placeholder="%"
                          autoFocus
                          className="w-14 text-xs border border-[var(--sl-primary)] rounded px-1.5 py-1 outline-none"
                          data-testid={`referral-discount-input-${u.id}`}
                        />
                        <button
                          onClick={() => saveDiscount(u.id)}
                          disabled={savingDiscount}
                          className="w-6 h-6 rounded grid place-items-center text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                          data-testid={`referral-discount-save-${u.id}`}
                          aria-label="Enregistrer"
                        >
                          {savingDiscount ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                        </button>
                        <button
                          onClick={() => saveDiscount(u.id, true)}
                          disabled={savingDiscount}
                          className="text-[10px] text-gray-400 hover:text-gray-600 underline"
                          data-testid={`referral-discount-reset-${u.id}`}
                          title="Remettre la réduction par défaut (selon le corridor)"
                        >
                          Défaut
                        </button>
                        <button
                          onClick={cancelEditDiscount}
                          disabled={savingDiscount}
                          className="w-6 h-6 rounded grid place-items-center text-gray-400 hover:bg-gray-100"
                          data-testid={`referral-discount-cancel-${u.id}`}
                          aria-label="Annuler"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 group">
                        {u.referral_discount_percent != null ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--sl-primary)] bg-[var(--sl-primary)]/10 px-2 py-0.5 rounded">
                            {u.referral_discount_percent}% perso
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Par défaut</span>
                        )}
                        <button
                          onClick={() => startEditDiscount(u)}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-[var(--sl-primary)] transition-opacity"
                          data-testid={`referral-discount-edit-btn-${u.id}`}
                          aria-label="Personnaliser la réduction accordée par le code"
                        >
                          <Pencil size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="py-3 pr-3 text-xs text-gray-500">{formatDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
